import { config } from '@/components/state-pill/config';
import { AdState } from '@/models/ad';
import { cx } from '@emotion/css';
import { FC } from 'react';
import { Trans } from 'react-i18next';

const noPulsingStates = ['DELETED', 'STOPPED'];
/**
 * StatePill component is used to display the state of an ad as a colored pill with a status dot and a label.
 *
 * @component
 * @param {Object} props - Component props
 * @param {AdState} props.state - The state of the ad to display (e.g., 'RUNNING', 'PAUSED', etc.).
 *
 * @example
 * <StatePill state="RUNNING" />
 *
 * The pill includes a color-coded dot (with a pulsing animation unless the state is 'DELETED' or 'STOPPED')
 * and a translated label according to the provided ad state.
 */

export const StatePill: FC<{
  state: AdState;
}> = ({ state }) => {
  const { textColor, bgColor, dotColor, labelKey } = config[state];

  return (
    <div className={cx('flex gap-2 px-3 py-0.5 rounded-full', bgColor)}>
      <div className="flex items-center gap-2">
        <span className="relative flex size-2">
          {!noPulsingStates.includes(state) ? (
            <span
              className={cx(
                'absolute inline-flex h-full w-full animate-ping rounded-full',
                dotColor
              )}
            ></span>
          ) : null}
          <span
            className={cx('relative inline-flex size-2 rounded-full', dotColor)}
          ></span>
        </span>
        <span
          className={cx(
            'font-medium text-xs md:text-sm lg:text-base',
            textColor
          )}
        >
          <Trans i18nKey={labelKey} />
        </span>
      </div>
    </div>
  );
};
